import React from 'react';
import {
  Code2,
  Layers,
  Cloud,
  Building2,
  Wrench,
  Zap,
  Shield,
  Users,
  Rocket,
  Database,
  GitBranch,
  Palette,
  Cpu,
  Server,
  Globe,
  Settings,
  Target,
  BarChart3,
  FlaskConical,
  Brain,
  Clock,
  AlertTriangle,
  Star,
  FileText,
  Package,
  TestTube,
  Smartphone,
  HardDrive,
  Network,
  Eye,
  Key,
  Search,
  ShieldCheck,
  Activity,
  Monitor
} from 'lucide-react';
import '../../styles/ServicePageCommon.css';
import './ProductEngineering.css';

const ProductEngineering = () => {
  return (
    <div className="product-engineering-service-page">
      {/* Main Content */}
      <section className="product-engineering-content-section">
        <div className="product-engineering-header">
          <h1 className="product-engineering-title">Product Engineering</h1>
          <p className="product-engineering-subtitle">
            "From idea to scale — engineered with precision, purpose, and platform thinking."
          </p>
        </div>

        <div className="product-engineering-content-layout">
          {/* Main Content Area */}
          <div className="product-engineering-main-content">

            {/* Platform Modernization Section */}
            <div className="product-engineering-section">
              <h2 className="product-engineering-section-title">Platform unification - Post M&A engagement</h2>
              <p className="product-engineering-tagline">
                "Legacy doesn't have to mean liability. Cospin helps you modernize monoliths, replatform critical systems, and unlock agility—without business disruption."
              </p>
              <div className="product-engineering-info-box">
                <p>
                  Many a time we noticed that most of the acquisitions stay alone. They are not merged, offered as single platform, or users had to go through multiple system experience to access single large platform.
                </p>
              </div>
              <div className="product-engineering-bullet-list">
                <div className="product-engineering-bullet-item">
                  <div className="product-engineering-bullet-icon">
                    <GitBranch size={20} strokeWidth={1.5} />
                  </div>
                  <span>Incremental modernization strategies (strangler pattern, containerization)</span>
                </div>
                <div className="product-engineering-bullet-item">
                  <div className="product-engineering-bullet-icon">
                    <Cloud size={20} strokeWidth={1.5} />
                  </div>
                  <span>Cloud migration, microservices, and API-first design</span>
                </div>
                <div className="product-engineering-bullet-item">
                  <div className="product-engineering-bullet-icon">
                    <Shield size={20} strokeWidth={1.5} />
                  </div>
                  <span>Deep experience in regulated domains like HealthTech, FinTech and MarTech</span>
                </div>
              </div>
              <p className="product-engineering-description">
                With a strategic lens on risk-reward and operational resilience, Cospin helps you modernize with confidence—not chaos.
              </p>
            </div>

            {/* R&D-as-a-Service Section */}
            <div className="product-engineering-section">
              <h2 className="product-engineering-section-title">R&D-as-a-Service</h2>
              <p className="product-engineering-tagline">
                "Cospin brings both imagination and execution muscle to your innovation roadmap."
              </p>
              <div className="product-engineering-highlight-box">
                <p>
                  Accelerate innovation without the overhead. Cospin's R&D-as-a-Service model helps you validate ideas, build prototypes, and explore emerging tech—without distracting your core teams.
                </p>
              </div>
              <p className="product-engineering-description">
                Enterprises always face constant challenge about where to focus. Is it to do with today's problem and planned roadmap or tomorrow's evolving customer needs, market trends and technology trends.
              </p>
              <p className="product-engineering-description">
                We have the answer. We have introduced R&D-as-a-Service engagement mainly to cater customers to focus on their core but still stay relevant to changing trends. Through solid amount of industry experience in solutioning customer asks around Product discovery, User journey mapping, Touch point analysis, Competitor gap study, Designing early access program, MVP and rollout.
              </p>
            </div>

            {/* Custom Development Section */}
            <div className="product-engineering-section">
              <h2 className="product-engineering-section-title">Custom development</h2>
              <p className="product-engineering-tagline">
                "Have a unique problem? We build unique solutions. From internal tools to customer-facing platforms, Cospin delivers custom software that fits your business like a glove."
              </p>
              <div className="product-engineering-two-column-layout">
                <div className="product-engineering-left-column">
                  <div className="product-engineering-icon-text">
                    <div className="product-engineering-icon-large">
                      <Palette size={32} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4>Full-stack capabilities</h4>
                      <p>Minimalist, scalable UI/UX with enterprise-grade security</p>
                    </div>
                  </div>
                </div>
                <div className="product-engineering-right-column">
                  <div className="product-engineering-icon-text">
                    <div className="product-engineering-icon-large">
                      <Users size={32} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4>Agile pods</h4>
                      <p>That integrate seamlessly with your teams</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="product-engineering-description">
                Unlike generic dev shops, Cospin brings product thinking, not just code. With leadership that understands both startup velocity and enterprise rigor, we build for impact—not rework.
              </p>
            </div>

            {/* Platform Engineering Section */}
            <div className="product-engineering-section">
              <h2 className="product-engineering-section-title">Core Platform engineering</h2>
              <p className="product-engineering-tagline">
                "Build once, scale infinitely. Cospin's platform engineering services help you create reusable, composable foundations—so every new product is faster, cheaper, and more reliable."
              </p>
              <div className="product-engineering-features-grid">
                <div className="product-engineering-feature-card">
                  <div className="product-engineering-feature-icon">
                    <Layers size={28} strokeWidth={1.5} />
                  </div>
                  <h4>Reusable service layers</h4>
                  <p>CI/CD pipelines, and observability baked in</p>
                </div>
                <div className="product-engineering-feature-card">
                  <div className="product-engineering-feature-icon">
                    <Cpu size={28} strokeWidth={1.5} />
                  </div>
                  <h4>Internal Developer Platforms (IDPs)</h4>
                  <p>And golden path design</p>
                </div>
                <div className="product-engineering-feature-card">
                  <div className="product-engineering-feature-icon">
                    <Palette size={28} strokeWidth={1.5} />
                  </div>
                  <h4>Building design systems</h4>
                  <p>Resusable  micro-frontend components for unified experience</p>
                </div>
                <div className="product-engineering-feature-card">
                  <div className="product-engineering-feature-icon">
                    <Users size={28} strokeWidth={1.5} />
                  </div>
                  <h4>Multi-tenancy</h4>
                  <p>Scalable architecture for data isolation</p>
                </div>
              </div>
              <p className="product-engineering-description">
                With founders who's architected scalable SaaS and health platforms, Cospin doesn't just build apps—we build ecosystems that evolve with your business.
              </p>
            </div>

            {/* SaaS Development Section */}
            <div className="product-engineering-section">
              <h2 className="product-engineering-section-title">SaaS platform development</h2>
              <p className="product-engineering-tagline">
                "From zero to SaaS. Whether you're building a new product or modernizing an existing one, Cospin helps you launch faster—with the right architecture, UX, and go-to-market readiness."
              </p>
              <div className="product-engineering-steps-layout">
                <div className="product-engineering-step">
                  <div className="product-engineering-step-number">1</div>
                  <div className="product-engineering-step-content">
                    <h4>MVP to scale</h4>
                    <p>Full-stack, cloud-native, secure</p>
                  </div>
                </div>
                <div className="product-engineering-step">
                  <div className="product-engineering-step-number">2</div>
                  <div className="product-engineering-step-content">
                    <h4>Product-market fit advisory</h4>
                    <p>Baked into the build process</p>
                  </div>
                </div>
                <div className="product-engineering-step">
                  <div className="product-engineering-step-number">3</div>
                  <div className="product-engineering-step-content">
                    <h4>Subscription, billing, and analytics</h4>
                    <p>Integrations out of the box</p>
                  </div>
                </div>
              </div>
              <p className="product-engineering-description">
                Cospin blends engineering excellence with strategic clarity. We understand SaaS org dynamics, equity tradeoffs, and what it takes to scale sustainably.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductEngineering;
